import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { maskVpa } from '../../utils/maskVpa';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import PrintIcon from '@mui/icons-material/Print';
import InfoIcon from '@mui/icons-material/Info';

const VendorAcemoneyTransactions = () => {
  const { shopId } = useParams();

  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shop, setShop] = useState(null);
  const [userLevel, setUserLevel] = useState(null);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
      } catch (err) {}
    }
  }, []);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRecords: 0,
    limit: 25
  });

  // Filters
  const [customerType, setCustomerType] = useState('all');
  const [status, setStatus] = useState('');
  const [paymentHandle, setPaymentHandle] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Transaction details dialog
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const componentRef = useRef();

  useEffect(() => {
    fetchTransactions();
  }, [pagination.currentPage, customerType, status, paymentHandle, searchQuery, paymentMode]); // [CASH-MODE] paymentMode

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    const diffInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    if (diffInDays > 90) {
      setDialogMessage('Date range cannot be more than 90 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  const fetchTransactions = async () => {
    if (!validateDates()) return;

    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.limit,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        ...(customerType !== 'all' && { customerType }),
        ...(status && { status }),
        ...(paymentHandle && { paymentHandle }),
        ...(searchQuery && { search: searchQuery }),
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };

      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/vendor/${shopId}`,
        { params }
      );

      if (response.data.success) {
        setShop(response.data.shop);
        setTransactions(response.data.transactions);
        setStatistics(response.data.statistics);
        // [CASH-MODE]
        setTotalsByMode(response.data.totalsByMode || (response.data.statistics && response.data.statistics.totalsByMode) || null);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setDialogMessage(
        'Failed to fetch transactions. Please try again.'
      );
      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage + 1 }));
  };

  const handleExport = async () => {
    try {
      const params = {
        format: 'csv',
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        shopId,
        ...(customerType !== 'all' && { customerType }),
        ...(status && { status }),
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };

      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/export`,
        { params }
      );

      setDialogMessage(`Export ready: ${response.data.count} transactions`);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error exporting:', error);
      setDialogMessage('Failed to export transactions');
      setOpenDialog(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewDetails = async (refId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/transaction/${refId}`
      );
      if (response.data.success) {
        setSelectedTransaction(response.data.transaction);
        setDetailsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      setDialogMessage('Failed to fetch transaction details');
      setOpenDialog(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getCustomerTypeColor = (type) => {
    return type === 'Registered' ? 'primary' : 'secondary';
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'Sl No',
      width: 70,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1 + (pagination.currentPage - 1) * pagination.limit
    },
    {
      field: 'refId',
      headerName: 'Reference ID',
      width: 180,
      renderCell: (params) => (
        <Tooltip title="Click to view details">
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer', color: 'primary.main' }}
            onClick={() => handleViewDetails(params.value)}
          >
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'customerType',
      headerName: 'Customer Type',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getCustomerTypeColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'payerName',
      headerName: 'Payer Name',
      width: 150
    },
    {
      field: 'payerVPA',
      headerName: 'Payer VPA',
      width: 180,
      renderCell: (params) => maskVpa(params.value, userLevel)
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => `₹${params.value ? params.value.toFixed(2) : '0.00'}`
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'transactionDate',
      headerName: 'Date',
      width: 150,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'dd/MM/yyyy HH:mm') : 'N/A'
    },
    {
      field: 'paymentHandle',
      headerName: 'Payment App',
      width: 120
    },
    // [CASH-MODE]
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 90,
      renderCell: (params) => <PaymentModeChip row={params.row} />
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleViewDetails(params.row.refId)}
        >
          <InfoIcon />
        </IconButton>
      )
    }
  ];

  // [CASH-MODE] client-side guard in case the API ignores the filter
  const rows = filterRowsByMode(transactions, paymentMode).map((txn, index) => ({
    id: index,
    ...txn
  }));

  // [CASH-MODE] API totals, else current page rows
  const modeTotals = totalsByMode || totalsByModeFromRows(rows, (r) => r.amount);
  const modeAmountKey = modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ padding: 3 }}>
        <BackButton />

        <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
          AceMoney Transactions Report
        </Typography>

        {shop && (
          <Paper sx={{ padding: 2, marginBottom: 2 }}>
            <Typography variant="h6">
              Shop: {shop.shopname} ({shop.shopCode || 'N/A'})
            </Typography>
          </Paper>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <Grid container spacing={2} sx={{ marginBottom: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    Registered Customers
                  </Typography>
                  <Typography variant="h4">
                    {statistics.registered.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success: {statistics.registered.successCount} |
                    Amount: ₹{statistics.registered.totalAmount ? statistics.registered.totalAmount.toFixed(2) : '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="secondary">
                    Non-Registered Customers
                  </Typography>
                  <Typography variant="h4">
                    {statistics.nonRegistered.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success: {statistics.nonRegistered.successCount} |
                    Amount: ₹{statistics.nonRegistered.totalAmount ? statistics.nonRegistered.totalAmount.toFixed(2) : '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Overall</Typography>
                  <Typography variant="h4">
                    {statistics.overall.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success: {statistics.overall.successCount} |
                    Amount: ₹{statistics.overall.totalAmount ? statistics.overall.totalAmount.toFixed(2) : '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* [CASH-MODE] */}
        <PaymentModeTotals
          totals={modeTotals}
          amountKey={modeAmountKey}
          title={totalsByMode ? 'Collection' : 'Collection (this page)'}
          sx={{ mb: 2 }}
        />

        {/* Filters */}
        <Paper sx={{ padding: 2, marginBottom: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Customer Type</InputLabel>
                <Select
                  value={customerType}
                  label="Customer Type"
                  onChange={(e) => setCustomerType(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="Registered">Registered</MenuItem>
                  <MenuItem value="Not Registered">Non-Registered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="SUCCESS">Success</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                  fetchTransactions();
                }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ marginTop: 1 }}>
            {/* [CASH-MODE] */}
            <Grid item xs={12} md={2}>
              <PaymentModeFilter
                value={paymentMode}
                onChange={(v) => {
                  setPaymentMode(v);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                size="medium"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search by RefID, VPA, Name, or Shop Code"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GetAppIcon />}
                onClick={handleExport}
              >
                Export
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* DataGrid */}
        <Paper ref={componentRef} sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            page={pagination.currentPage - 1}
            pageSize={pagination.limit}
            rowCount={pagination.totalRecords}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[25]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </Paper>

        {/* Message Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Information</DialogTitle>
          <DialogContent>
            <Typography>{dialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transaction Details Dialog */}
        <Dialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Transaction Details</DialogTitle>
          <DialogContent>
            {selectedTransaction && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reference ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTransaction.refId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTransaction.transactionId || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Customer Type
                    </Typography>
                    <Chip
                      label={selectedTransaction.customerType}
                      color={getCustomerTypeColor(selectedTransaction.customerType)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedTransaction.status}
                      color={getStatusColor(selectedTransaction.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payer Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTransaction.payerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payer VPA
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {maskVpa(selectedTransaction.payerVPA, userLevel)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      ₹{selectedTransaction.amount ? selectedTransaction.amount.toFixed(2) : '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Handle
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTransaction.paymentHandle || 'N/A'}
                    </Typography>
                  </Grid>
                  {/* [CASH-MODE] */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Mode
                    </Typography>
                    <PaymentModeChip row={selectedTransaction} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Transaction Date
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTransaction.transactionDate
                        ? format(new Date(selectedTransaction.transactionDate), 'dd/MM/yyyy HH:mm:ss')
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedTransaction.failureReason && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="error">
                        Failure Reason
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedTransaction.failureReason}
                      </Typography>
                    </Grid>
                  )}
                  {selectedTransaction.remarks && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Remarks
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedTransaction.remarks}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default VendorAcemoneyTransactions;
